import Vue from 'vue';
import Meta from 'vue-meta';
import Router from 'vue-router';
import { sync } from 'vuex-router-sync';
import store from '@/store';
import routes from './routes';
import { ability } from '@/store';
import User from "@/services/User/User";

Vue.use(Meta);
Vue.use(Router);

const router = make(routes({ authGuard, guestGuard }));

if (store) {
  sync(store, router);
}

export default router;

/**
 * Create a new router instance.
 *
 * @param  {Array} routes
 * @return {Router}
 */
function make(routes) {
  const router = new Router({
    routes,
    scrollBehavior,
    mode: 'history',
    base: process.env.BASE_URL,
  });

  // Register before guard.
  router.beforeEach(async (to, from, next) => {
    const userLoggedIn = store.getters.authCheck;
    Vue.$model = to.meta.model;

    const { meta } = to;
    if (meta.progress !== undefined) {
      const { progress } = meta;
      // parse meta tags
      router.app.$Progress.parseMeta(progress);
    }
    //  start the progress bar
    router.app.$Progress.start();

    // If the token has expired.. log them out
    if (userLoggedIn && User.hasExpiredToken(store.getters.authExpiry)) {
      try {
        await store.dispatch('logout', { expired: true });
      } catch (e) {
        console.log(e);
      }
    } else {
      const isOnLoginPage = to.name === 'login';

      if (!store.getters.authCheck && store.getters.authToken && !isOnLoginPage) {
        try {
          await store.dispatch('fetchUser');
        } catch (e) {
          console.log(e);
        }
      }

      // If user is logged in but has no clientId set, and is not on the login screen
      if (userLoggedIn && !store.getters.userClientId && !isOnLoginPage) {
        try {
          await store.dispatch('logout', { expired: true });
        } catch (e) {
          console.log(e);
        }
      }

      // Control route permissions
      if (meta.permissions) {
        const { permissions } = meta;
        permissions.forEach((p) => {
          if (ability.cannot(p.action, p.subject)) {
            Vue.notify({
              title: 'Access denied',
              text: 'You do not have access to this area',
              type: 'error',
            });

            next({ name: 'dashboard' });
          }
        });
      }

      next();
    }
  });

  // Register after hook.
  router.afterEach((to, from) => {
    // For convenience, we add the class name of the route to the body
    document.body.className = to.name || document.body.className;
    if (to.meta && to.meta.title) {
      document.title = to.meta.title;
    }
    router.app.$nextTick(() => {
      // router.app.$loading.finish();
      router.app.$Progress.finish();
    });
  });

  return router;
}

/**
 * Redirect to login if guest.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function authGuard(routes) {
  return beforeEnter(routes, async (to, from, next) => {

    const { redirect } = store.getters;

    if (!store.getters.authCheck) {
      next({ name: 'home' });
    } else if (redirect) {
      // Reset the redirect as we are done here
      await store.dispatch('setRedirect', null);

      next(redirect);
    } else {
      // Check terms and conditions
      next();
    }
  });
}

/**
 * Redirect home if authenticated.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function guestGuard(routes) {
  return beforeEnter(routes, async (to, from, next) => {
    if (store.getters.authCheck) {
      next({ name: 'dashboard' });
    } else {
      // If a user was trying to reach a specific page before logging in, we record
      // it here so we can redirect the user later
      const redirect = window.location.pathname;

      // We currently want to restrict this logic to only the /eula page
      if (redirect === '/eula') {
        await store.dispatch('setRedirect', redirect);
      }
      next();
    }
  });
}

/**
 * Apply beforeEnter guard to the routes.
 *
 * @param  {Array} routes
 * @param  {Function} beforeEnter
 * @return {Array}
 */
function beforeEnter(routes, beforeEnter) {
  return routes.map(route => ({ ...route, beforeEnter }));
}

/**
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior(to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition;
  }

  const position = {};

  if (to.hash) {
    position.selector = to.hash;
  }

  if (to.matched.some(m => m.meta.scrollToTop)) {
    position.x = 0;
    position.y = 0;
  }

  return position;
}
